/**
 * @format
 */

import * as types from './actions';

const initialState = {
  // state used when task is being added
  addTaskLoading: false,
  addTaskError: null,

  // state used when task is being updated
  updateTaskLoading: false,
  updateTaskError: null,

  // delete task
  deleteTaskLoading: false,
  deleteTaskError: null,

  // task loading
  taskLoading: false,
  taskListLoadingState: false,

  // task error
  taskError: null,

  // task state with key of task_id & value of task object
  tasks: {},
  tasksOrder: [],
  isNonEmptyFilterSortApplied: false,
};

export default (state = initialState, { type, payload }) => {
  switch (type) {
    // Create Task
    case types.CREATE_TASK_LOADING:
      return Object.assign({}, state, {
        addTaskLoading: true,
        addTaskError: null,
      });

    case types.CREATE_TASK_FAILURE:
      return Object.assign({}, state, {
        addTaskLoading: false,
        addTaskError: payload,
      });

    case types.CREATE_TASK_SUCCESS:
      return Object.assign({}, state, {
        addTaskLoading: false,
        addTaskError: null,
        tasksOrder:
          payload.task.parent_id === null
            ? [...state.tasksOrder, payload.task.id]
            : state.tasksOrder,
        tasks:
          payload.task.parent_id === null
            ? {
                [payload.taskId]: payload.task,
                ...state.tasks,
              }
            : {
                ...state.tasks,
                [payload.task.parent_id]: {
                  ...state.tasks[payload.task.parent_id],
                  sub_tasks: (() => {
                    const existingSubTasks =
                      state.tasks[payload.task.parent_id].sub_tasks || [];
                    const subTaskIndex = existingSubTasks.findIndex(
                      sub => sub.id === payload.taskId
                    );

                    if (subTaskIndex !== -1) {
                      // Update existing sub-task
                      return existingSubTasks.map(sub =>
                        sub.id === payload.taskId ? payload.task : sub
                      );
                    } else {
                      // Add new sub-task
                      return [payload.task, ...existingSubTasks];
                    }
                  })(),
                },
              },
      });

    // Get all task
    case types.GET_ALL_TASKS_LOADING:
      return Object.assign({}, state, {
        taskListLoadingState: true,
      });

    case types.GET_ALL_TASKS_FAILURE:
      return Object.assign({}, state, {
        taskListLoadingState: false,
        taskError: payload.message,
      });

    case types.GET_ALL_TASKS_SUCCESS:
      return Object.assign({}, state, {
        taskListLoadingState: false,
        taskError: null,
        tasks: {
          ...payload.tasks?.reduce((result, item) => {
            result[item.id] = state.tasks[item.id]
              ? {
                  ...state.tasks[item.id],
                  ...item,
                }
              : item;
            return result;
          }, {}),
        },
        isNonEmptyFilterSortApplied: payload.isEmptyFilters,
        tasksOrder: payload.tasks.map(({ id }) => id),
      });

    // Get task by Id
    case types.GET_TASK_LOADING:
      return Object.assign({}, state, {
        taskLoading: true,
        taskError: null,
      });
    case types.GET_TASK_FAILURE:
      return Object.assign({}, state, {
        taskLoading: false,
        taskError: payload.message,
      });
    case types.GET_TASK_SUCCESS:
      return Object.assign({}, state, {
        taskLoading: false,
        taskError: null,
        tasks: { ...state.tasks, [payload.task.id]: payload.task },
      });

    // Update tasks
    case types.OPTIMISTIC_TASK_UPDATE: {
      const { updatedTasks: payloadTasks, newTasksOrder } = payload;
      const updatedTasks = {
        ...state.tasks,
      };
      const subtasksByParent = {};

      for (const item of payloadTasks) {
        if (item.parent_id === null) {
          // Top-level task
          updatedTasks[item.id] = {
            ...updatedTasks[item.id],
            ...item,
          };
        } else {
          // Group subtasks by parent
          if (!subtasksByParent[item.parent_id]) {
            subtasksByParent[item.parent_id] = [];
          }
          subtasksByParent[item.parent_id].push(item);
        }
      }

      // Apply all grouped subtask updates per parent
      for (const [parentId, updatedSubtasks] of Object.entries(
        subtasksByParent
      )) {
        const parent = updatedTasks[parentId];
        if (!parent || !Array.isArray(parent.sub_tasks)) continue;

        const updatedSubTasks = parent.sub_tasks.map(sub => {
          const update = updatedSubtasks.find(s => s.id === sub.id);
          return update ? { ...sub, ...update } : sub;
        });

        updatedTasks[parentId] = {
          ...parent,
          sub_tasks: updatedSubTasks,
        };
      }

      if (Array.isArray(newTasksOrder)) {
        newTasksOrder.map(function(taskId, index) {
          updatedTasks[taskId].position_id = index + 1;
          return 0;
        });
      }

      return {
        ...state,
        updateTaskLoading: true,
        tasks: updatedTasks,
        tasksOrder: Array.isArray(newTasksOrder)
          ? newTasksOrder
          : state.tasksOrder,
      };
    }

    case types.ROLLBACK_TASK_UPDATE:
      return {
        ...state,
        tasks: payload, // restore old state
      };

    case types.UPDATE_TASK_SUCCESS:
      return Object.assign({}, state, {
        taskListLoadingState: false,
        updateTaskLoading: false,
        taskError: null,
        tasks: {
          ...state.tasks, // Keep previous state
          ...payload.tasks?.reduce((result, item) => {
            if (item.parent_id === null) {
              result[item.id] = state.tasks[item.id]
                ? {
                    ...state.tasks[item.id], // Preserve existing data
                    ...item, // Overwrite with updated fields
                  }
                : item;
            } else {
              const { parent_id } = item;
              const parentTask = state.tasks[parent_id] || {};
              const selectedSubTaskIndex = parentTask.sub_tasks.findIndex(
                sTask => sTask.id === item.id
              );
              parentTask.sub_tasks[selectedSubTaskIndex] = item;
              result[parent_id] = parentTask;
            }
            return result;
          }, {}),
        },
        tasksOrder: Array.isArray(payload.newTasksOrder)
          ? payload.newTasksOrder
          : state.tasksOrder,
      });
    case types.UPDATE_TASK_FAILURE:
      return Object.assign({}, state, {
        updateTaskLoading: false,
        updateTaskError: payload.message,
      });

    case types.OPTIMISTIC_DELETE_TASK: {
      const { taskId, backup } = payload;

      const tasksFiltered = Object.keys(state.tasks)
        .filter(id => id !== taskId)
        .reduce((obj, id) => {
          obj[id] = { ...state.tasks[id] };
          return obj;
        }, {});

      Object.keys(tasksFiltered).forEach(id => {
        const task = tasksFiltered[id];
        if (Array.isArray(task.sub_tasks)) {
          task.sub_tasks = task.sub_tasks.filter(
            subtask => subtask.id !== taskId
          );
        }
      });

      return {
        ...state,
        tasks: tasksFiltered,
        deleteTaskError: null,
        // Optionally track last deleted in case of undo
        lastDeletedTaskBackup: backup,
      };
    }

    case types.REVERT_DELETE_TASK: {
      const { taskId, backup } = payload;
      const tasks = { ...state.tasks };

      if (backup.parent_id) {
        // Revert subtask at its original index
        const parent = tasks[backup.parent_id];
        if (parent) {
          const subTasks = [...parent.sub_tasks];
          subTasks.splice(backup.__index, 0, backup); // insert at original index
          parent.sub_tasks = subTasks;
        }
      } else {
        // Revert main task at original position

        const entries = Object.entries(tasks);
        entries.splice(backup.__index, 0, [taskId, backup]); // insert at original index

        const restoredTasks = entries.reduce((acc, [id, val]) => {
          acc[id] = val;
          return acc;
        }, {});

        return {
          ...state,
          tasks: restoredTasks,
          deleteTaskError: 'Failed to delete task. Changes reverted.',
        };
      }

      return {
        ...state,
        tasks,
        deleteTaskError: 'Failed to delete task. Changes reverted.',
      };
    }

    case types.DELETE_TASK_SUCCESS:
      return {
        ...state,
        deleteTaskLoading: false,
        deleteTaskError: null,
        // No need to change `tasks` because it was already removed optimistically
      };

    case types.DELETE_TASK_FAILURE:
      return Object.assign({}, state, {
        deleteTaskLoading: false,
        deleteTaskError: payload.message,
      });

    default:
      return state;
  }
};
